import React, { useState, useEffect } from "react";
import { Button, Form, Icon } from "tabler-react";
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import { NotificationManager } from 'react-notifications';
import FixRequiredSelect from "../../components/validations/FixRequiredSelect";
import BaseSelect from "react-select";
import "./TractosForm.css";

const TractosForm = ({
  accion,
  elementos,
  cerrarModal,
  onSave,
  idTransportista: idTransportistaProp,
  // Cuando es true (solo catálogo normal de tractos), se crea una relación por
  // cada plataforma del usuario (transportista de menor id por plataforma).
  // En cualquier otro flujo (naviero, etc.) se liga solo a idTransportista.
  multiPlataforma = false
}) => {
  const [formulario, setFormulario] = useState({
    placa: "",
    economico: "",
    marca: null,
    color: null,
    modelo: null
  });

  const [marcaOpciones, setMarcaOpciones] = useState([]);
  const [modeloOpciones, setModeloOpciones] = useState([]);
  const [colorsOpciones, setColorsOpciones] = useState([]);
  const [crudbutonEnable, setCrudbutonEnable] = useState(true);
  const [idTransportista, setIdTransportista] = useState(null);
  // Un transportista representativo (menor id) por cada plataforma del usuario.
  const [transportistasPorPlataforma, setTransportistasPorPlataforma] = useState([]);

  useEffect(() => {
    // Obtener idTransportista de la prop (flujo edición / naviero): representa
    // el transportista específico al que se liga la unidad.
    if (idTransportistaProp) {
      setIdTransportista(idTransportistaProp);
    }

    // Calcular SIEMPRE un transportista por plataforma (menor id) para poder
    // crear una relación por cada plataforma del usuario. Esto debe hacerse
    // aunque llegue idTransportistaProp, porque en creación se requieren N
    // relaciones (una por plataforma), no una sola.
    const usuario = JSON.parse(sessionStorage.getItem("usuario"));
    const currentUserSession = JSON.parse(sessionStorage.getItem("currentUser"));
    if (usuario && currentUserSession) {
      const plataformasIds = [...new Set(currentUserSession.map(c => c.plataforma?.id).filter(Boolean))];
      Promise.all(plataformasIds.map(idPlat =>
        Crud_Catalogos("usuarios_transportistas/usuario/" + usuario.id + "/plataforma/" + idPlat, "", "get", "", [], "", "", "", [])
          .then(res => ({ idPlataforma: idPlat, transportistas: res || [] }))
          .catch(() => ({ idPlataforma: idPlat, transportistas: [] }))
      )).then((resultados) => {
        // Por cada plataforma, elegir UN transportista (el de menor id -> determinista)
        const seleccion = resultados
          .map(r => {
            const ids = [...new Set(
              (r.transportistas || [])
                .filter(t => t.idTransportista !== null)
                .map(t => t.idTransportista)
            )].sort((a, b) => a - b);
            return ids.length > 0 ? { idPlataforma: r.idPlataforma, idTransportista: ids[0] } : null;
          })
          .filter(Boolean);

        setTransportistasPorPlataforma(seleccion);
        // Si no llegó prop, usar el primero como idTransportista de respaldo.
        if (!idTransportistaProp && seleccion.length > 0) {
          setIdTransportista(seleccion[0].idTransportista);
        }
      }).catch(err => console.error("Error al cargar transportista:", err));
    }

    // Cargar años para modelo
    let startYear = 1960;
    let year = new Date().getFullYear();
    let opcionesAnio = [];
    for (let i = year; i > startYear; i--) {
      opcionesAnio.push({value: i, label: i});
    }
    setModeloOpciones(opcionesAnio);

    // Cargar colores
    Crud_Catalogos("colores", "", "get", "", [], "", "", "", []).then((returnVal) => {
      let colorsTemp = [{label: "Selecciona una opción", value: ""}];
      returnVal.forEach(color => {
        colorsTemp.push({
          label: color.nombreColor,
          value: color.nombreColor,
          id: color.idColor,
          styles: "color:" + color.hex
        });
      });
      setColorsOpciones(colorsTemp);
    });

    // Cargar marcas activas
    const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
    const idPerfil = currentUser[0].perfil.id;
    const esTransportista = idPerfil === 2925 || idPerfil === 4608;
    const marcasEndpoint = esTransportista ? "unidadesMarcasActivas/perfil/" + idPerfil : "unidadesMarcasActivas";

    Crud_Catalogos(marcasEndpoint, "", "get", "", [], "", "", "", []).then((returnVal) => {
      let marcasTemp = [{label: "Selecciona una opción", value: ""}];
      returnVal.forEach(marca => {
        marcasTemp.push({label: marca.nombre, value: marca.nombre, id: marca.id});
      });
      setMarcaOpciones(marcasTemp);
    });

    // Si es edición, cargar datos
    if (accion === "editar" && elementos) {
      setFormulario({
        placa: elementos.placa || "",
        economico: elementos.economico || "",
        marca: elementos.idUnidadMarca ? {
          label: elementos.idUnidadMarca.nombre, 
          value: elementos.idUnidadMarca.nombre, 
          id: elementos.idUnidadMarca.id
        } : null,
        color: elementos.color ? {
          label: elementos.color, 
          value: elementos.color, 
          id: elementos.idColor?.idColor
        } : null,
        modelo: elementos.modelo ? {
          label: elementos.modelo, 
          value: parseInt(elementos.modelo)
        } : null
      });
    }
  }, []);

  const SelectMarca = props => (
    <FixRequiredSelect
      {...props}
      SelectComponent={BaseSelect}
      options={marcaOpciones}
    />
  );

  const SelectModelo = props => (
    <FixRequiredSelect
      {...props}
      SelectComponent={BaseSelect}
      options={modeloOpciones}
    />
  );

  const SelectColor = props => (
    <FixRequiredSelect
      {...props}
      SelectComponent={BaseSelect}
      options={colorsOpciones}
    />
  );

  // Función para vincular un tracto existente al transportista actual
  const vincularTractoExistente = async (placa, idTransp) => {
    try {
      // Buscar el tracto por placa en todos los tractos
      const todosTractos = await Crud_Catalogos("unidadTerNav", "", "get", "", [], "", "", "", []);
      const tractoExistente = todosTractos.find(
        t => t.placa?.toUpperCase().trim() === placa.toUpperCase().trim()
      );

      if (!tractoExistente) {
        NotificationManager.error("Error al guardar el tracto", "Error");
        setCrudbutonEnable(true);
        return;
      }

      // Verificar si ya tiene relación activa con este transportista
      const relaciones = await Crud_Catalogos(
        "transportistas_unidades_ter_nav/id_unidad/" + tractoExistente.id, "", "get", "", [], "", "", "", []
      );

      const relacionActiva = Array.isArray(relaciones) && relaciones.find(
        r => r.idTransportista === idTransp && r.estatus !== false
      );

      if (relacionActiva) {
        NotificationManager.info(
          "Este tracto ya existe en tu catálogo",
          "Tracto existente"
        );
        setCrudbutonEnable(true);
        return;
      }

      // Verificar si tiene relación desactivada con este transportista (para reactivar)
      const relacionDesactivada = Array.isArray(relaciones) && relaciones.find(
        r => r.idTransportista === idTransp && r.estatus === false
      );

      if (relacionDesactivada) {
        // Reactivar la relación existente
        await Crud_Catalogos(
          "transportistas_unidades_ter_nav/activar/" + tractoExistente.id + "/" + idTransp,
          "", "put", "", {}, "", "", "", []
        );
        NotificationManager.success("Tracto vinculado exitosamente a tu catálogo", "Éxito");
        if (onSave) {
          onSave({
            id: tractoExistente.id,
            placa: tractoExistente.placa,
            economico: tractoExistente.economico,
            color: tractoExistente.color,
            modelo: tractoExistente.modelo,
            idUnidadMarca: tractoExistente.idUnidadMarca,
          });
        }
        cerrarModal();
        return;
      }

      // No tiene relación → crear nueva
      await Crud_Catalogos("transportistas_unidades_ter_nav", "", "post", "", {
        idUnidad: tractoExistente.id,
        idTransportista: idTransp
      }, "", "", "", []);

      NotificationManager.success("Tracto vinculado exitosamente a tu catálogo", "Éxito");
      if (onSave) {
        onSave({
          id: tractoExistente.id,
          placa: tractoExistente.placa,
          economico: tractoExistente.economico,
          color: tractoExistente.color,
          modelo: tractoExistente.modelo,
          idUnidadMarca: tractoExistente.idUnidadMarca,
        });
      }
      cerrarModal();

    } catch (error) {
      console.error("Error al vincular tracto existente:", error);
      NotificationManager.error("Error al vincular el tracto", "Error");
      setCrudbutonEnable(true);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setCrudbutonEnable(false);

    const tractoDTO = {
      placa: (formulario.placa || "").trim().toUpperCase(),
      economico: (formulario.economico || "").trim(),
      color: formulario.color?.value || "",
      modelo: formulario.modelo?.value?.toString() || "",
      idUnidadMarca: {
        id: formulario.marca?.id
      },
      idColor: formulario.color?.id ? {
        idColor: formulario.color.id
      } : null,
      idUnidadTipo: {
        id: 28
      },
      idUnidadEstatusOperacion: {
        id: 1
      },
      estatus: true
    };

    // Si es edición, agregar el ID
    if (accion === "editar" && elementos?.id) {
      tractoDTO.id = elementos.id;
    }

    if (accion === "editar") {
      if (idTransportista) {
        // Usar endpoint con validación
        Crud_Catalogos(`unidadTerNav/validar/${idTransportista}`, "", "put", "", tractoDTO, "", "", "", [])
          .then(() => {
            NotificationManager.success("Tracto actualizado exitosamente", "Éxito");
            if (onSave) {
              onSave({
                id: elementos.id,
                placa: tractoDTO.placa,
                economico: tractoDTO.economico,
                color: tractoDTO.color,
                modelo: tractoDTO.modelo,
                idUnidadMarca: tractoDTO.idUnidadMarca,
              });
            }
            cerrarModal();
          })
          .catch(err => {
            console.error("Error al actualizar tracto con validación, intentando sin validación:", err);
            // Fallback: intentar con endpoint directo por ID
            Crud_Catalogos(`unidadTerNav/${tractoDTO.id}`, "", "put", "", tractoDTO, "", "", "", [])
              .then(() => {
                NotificationManager.success("Tracto actualizado exitosamente", "Éxito");
                if (onSave) {
                  onSave({
                    id: elementos.id,
                    placa: tractoDTO.placa,
                    economico: tractoDTO.economico,
                    color: tractoDTO.color,
                    modelo: tractoDTO.modelo,
                    idUnidadMarca: tractoDTO.idUnidadMarca,
                  });
                }
                cerrarModal();
              })
              .catch(err2 => {
                console.error("Error al actualizar tracto:", err2);
                NotificationManager.error(
                  err2.response?.data?.message || err2.response?.data || "Error al actualizar el tracto",
                  "Error"
                );
                setCrudbutonEnable(true);
              });
          });
      } else {
        // Sin idTransportista, usar endpoint directo por ID
        Crud_Catalogos(`unidadTerNav/${tractoDTO.id}`, "", "put", "", tractoDTO, "", "", "", [])
          .then(() => {
            NotificationManager.success("Tracto actualizado exitosamente", "Éxito");
            if (onSave) {
              onSave({
                id: elementos.id,
                placa: tractoDTO.placa,
                economico: tractoDTO.economico,
                color: tractoDTO.color,
                modelo: tractoDTO.modelo,
                idUnidadMarca: tractoDTO.idUnidadMarca,
              });
            }
            cerrarModal();
          })
          .catch(err => {
            console.error("Error al actualizar tracto:", err);
            NotificationManager.error(
              err.response?.data?.message || err.response?.data || "Error al actualizar el tracto",
              "Error"
            );
            setCrudbutonEnable(true);
          });
      }
    } else {
      // Crear nuevo:
      // - Flujo naviero (llega idTransportistaProp): el tracto se liga SOLO al
      //   transportista del movimiento.
      // - Catálogo normal (sin prop): el tracto físico se crea UNA vez y se
      //   relaciona con un transportista por cada plataforma (menor id por plataforma).
      const idsTransportista = (multiPlataforma && transportistasPorPlataforma.length > 0)
        ? transportistasPorPlataforma.map(sel => sel.idTransportista)
        : [idTransportistaProp || idTransportista];
      const idsTransportistaUnicos = [...new Set(idsTransportista.filter(Boolean))];

      Crud_Catalogos("unidadTerNav", "", "post", "", tractoDTO, "", "", "", [])
        .then((response) => {
          if (idsTransportistaUnicos.length > 0 && response.id) {
            Promise.all(idsTransportistaUnicos.map(idT =>
              Crud_Catalogos("transportistas_unidades_ter_nav", "", "post", "", {
                idUnidad: response.id,
                idTransportista: idT
              }, "", "", "", [])
            ))
              .then(() => {
                NotificationManager.success(
                  `Tracto creado exitosamente (${idsTransportistaUnicos.length} relación${idsTransportistaUnicos.length === 1 ? "" : "es"})`,
                  "Éxito"
                );
                if (onSave) {
                  onSave({
                    id: response.id,
                    placa: tractoDTO.placa,
                    economico: tractoDTO.economico,
                    color: tractoDTO.color,
                    modelo: tractoDTO.modelo,
                    idUnidadMarca: tractoDTO.idUnidadMarca,
                  });
                }
                cerrarModal();
              })
              .catch(err => {
                console.error("Error al crear relación:", err);
                NotificationManager.warning("Tracto creado pero no se pudo asignar a todos los transportistas", "Advertencia");
                if (onSave) {
                  onSave({
                    id: response.id,
                    placa: tractoDTO.placa,
                    economico: tractoDTO.economico,
                    color: tractoDTO.color,
                    modelo: tractoDTO.modelo,
                    idUnidadMarca: tractoDTO.idUnidadMarca,
                  });
                }
                cerrarModal();
              });
          } else {
            NotificationManager.success("Tracto creado exitosamente", "Éxito");
            if (onSave) {
              onSave({
                id: response.id,
                placa: tractoDTO.placa,
                economico: tractoDTO.economico,
                color: tractoDTO.color,
                modelo: tractoDTO.modelo,
                idUnidadMarca: tractoDTO.idUnidadMarca,
              });
            }
            cerrarModal();
          }
        })
        .catch(err => {
          console.error("Error al crear tracto (posible duplicado):", err);
          // Si el backend rechaza, intentar vincular el tracto existente
          if (idTransportista) {
            vincularTractoExistente(tractoDTO.placa, idTransportista);
          } else {
            NotificationManager.error(
              err.response?.data?.message || err.response?.data || "Error al guardar el tracto",
              "Error"
            );
            setCrudbutonEnable(true);
          }
        });
    }
  };



  return (
    <div className="tracto-form-container">
      <div className="tracto-header">
        <Icon name="truck" className="header-icon" />
        <h2>{accion === "nuevo" ? "Nuevo Tracto" : "Editar Tracto"}</h2>
      </div>

      <Form onSubmit={handleSubmit} className="tracto-form">
        <div className="form-section">
          <div className="form-row">
            <div className="form-field">
              <label className="field-label">
                <Icon name={accion === "editar" ? "lock" : "hash"} />
                Placa {accion === "editar" && <span style={{ fontSize: '11px', color: '#888', marginLeft: '4px' }}>(no modificable)</span>}
              </label>
              <Form.Input 
                name="placa" 
                required 
                value={formulario.placa}
                readOnly={accion === "editar"}
                onChange={(e) => accion !== "editar" && setFormulario({...formulario, placa: e.target.value.toUpperCase()})}
                placeholder="Ej: ABC123"
                style={accion === "editar" ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
              />
            </div>

            <div className="form-field">
              <label className="field-label">
                <Icon name="tag" />
                Económico
              </label>
              <Form.Input 
                name="economico" 
                required 
                value={formulario.economico}
                onChange={(e) => setFormulario({...formulario, economico: e.target.value})}
                placeholder="Ej: TRC-001"
              />
            </div>
          </div>

          <div className="form-field">
            <label className="field-label">
              <Icon name="award" />
              Marca
            </label>
            <BaseSelect
              name="marca" 
              value={formulario.marca}
              onChange={(val) => { setFormulario(prev => ({...prev, marca: val})); }}
              options={marcaOpciones}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 100001 }) }}
              blurInputOnSelect={false}
              placeholder="Selecciona una opción"
            />
          </div>

          <div className="form-field">
            <label className="field-label">
              <Icon name="circle" />
              Color
            </label>
            <BaseSelect
              name="color" 
              value={formulario.color}
              onChange={(val) => { setFormulario(prev => ({...prev, color: val})); }}
              options={colorsOpciones}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 100001 }) }}
              blurInputOnSelect={false}
              placeholder="Selecciona una opción"
            />
          </div>

          <div className="form-field">
            <label className="field-label">
              <Icon name="calendar" />
              Modelo (Año)
            </label>
            <BaseSelect
              name="modelo" 
              value={formulario.modelo}
              onChange={(val) => { setFormulario(prev => ({...prev, modelo: val})); }}
              options={modeloOpciones}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 100001 }) }}
              blurInputOnSelect={false}
              placeholder="Selecciona una opción"
            />
          </div>
        </div>

        <div className="form-actions">
          <Button type="submit" color="primary" disabled={!crudbutonEnable}>
            <Icon name="save" />
            {accion === "nuevo" ? "Guardar" : "Actualizar"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TractosForm;
